import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCatalogKpis, type CatalogKpiSnapshot } from '../infra/catalogKpiRepo';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

export type CatalogKpis = {
  totalProducts: number | null;
  onMenuCount: number | null;
  servicesCount: number | null;
  alertsCount: number | null;
  incompleteCount: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

/**
 * KPIs globales del catálogo — independientes de los filtros del listado.
 *
 * Se cargan al montar y al cambiar de sucursal; se refrescan:
 * - Tras eventos de sync (`subscribeCatalogSynced`)
 * - Manualmente vía `refresh()` tras mutaciones locales (create, edit, delete, CSV)
 *
 * NO se recalculan al filtrar/buscar — son métricas del catálogo completo
 * (salvo el tile «Resultados» en UI, que usa el total del listado filtrado).
 *
 * Ante un error se conserva el último snapshot válido (no mostrar ceros falsos).
 * Si nunca se pudo cargar, los conteos quedan en null → los tiles muestran «—».
 */
export function useCatalogKpis(): CatalogKpis {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const [snapshot, setSnapshot] = useState<CatalogKpiSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);
  const generationRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generationRef.current;
    setLoading(true);
    try {
      const result = await fetchCatalogKpis({ includeStockAlerts: inventoryStockUxEnabled });
      if (gen !== generationRef.current || !mountedRef.current) return;
      setSnapshot(result);
    } catch (error) {
      console.warn('[CatalogKpis] No se pudieron cargar los KPIs', { branchId, error });
      // No pisar el último snapshot válido con ceros — la UI decide «—» o stale.
    } finally {
      if (gen === generationRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, [branchId, inventoryStockUxEnabled]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const unsubscribe = subscribeCatalogSynced(() => {
      void load();
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [load, branchId]);

  return {
    totalProducts: snapshot?.totalProducts ?? null,
    onMenuCount: snapshot?.onMenuCount ?? null,
    servicesCount: snapshot?.servicesCount ?? null,
    alertsCount: snapshot?.alertsCount ?? null,
    incompleteCount: snapshot?.incompleteCount ?? null,
    loading,
    refresh: load,
  };
}
